// ============================================================================
// Lifespan v1 — engine types.
// Every runtime value the engine touches is a plain, JSON-serializable struct
// (migration contract §2: no class instances, no closures inside state). This
// is what lets the same State cross the wire into a v2 PlayerDO unchanged.
// ============================================================================

/** Work mode chosen at the annual review (GDD §4.2). */
export type Mode = 'sab' | 'std' | 'ot';

/** Micro-card kind (GDD §8E.1). */
export type CardType = 'spend' | 'earn';

/**
 * Card-acceptance policy — the G10 family. `default` = decline everything
 * (status quo, identical to never logging in).
 */
export type CardPolicy = 'default' | 'greedy' | 'spendthrift' | 'acceptAll' | 'myopic';

/** The complete ParamSet (GDD §10). One versioned source of truth. */
export interface Params {
  paramSetVersion: string;

  // start state (§1–§3)
  startAge: number;
  maxAge: number;
  S0: number;
  M0: number;
  H0: number;

  // savings (§4)
  r: number;
  rDebt: number;
  debtFloor: number;
  debtRelease: number;

  // wages (§5), tier 0..6
  wages: number[];

  // cost of living (§6)
  colBase: number;
  colSlope: number;

  // work-mode / study income multipliers (§7)
  modeMult: Record<Mode, number>;
  studyMult: number;

  // morale (§8–§10)
  kM: number;
  drain: Record<Mode, number>;
  studyDrain: number;
  expCap: number;
  expScaleFrac: number;

  // gates (§11–§13)
  gateLo: number;
  gateSpan: number;
  enrollM: number;
  enrollMSkills: number[];
  /**
   * Burnout capacity band (v1.2.0 — the smoothed step). Capacity degrades
   * LINEARLY from 1.0 at M ≥ burnoutHi down to burnoutMult at M ≤ burnoutLo:
   * a continuous penalty in M, so there is no income/health discontinuity to
   * "surf" by reacting to observed morale (no-vigilance philosophy #1). The
   * frozen-flag substep contract still evaluates the factor once per substep.
   */
  burnoutHi: number;
  burnoutLo: number;
  /** capacity floor: the income multiplier at and below burnoutLo */
  burnoutMult: number;

  // skills (§14–§17)
  studyYears: number[];
  tuition: number[];
  sabRequiredForSkills: number[];
  sabMinYears: number;
  progressDecayRate: number;
  progressEpsilon: number;
  dormantDecayEnabled: boolean;

  // health (§18–§22)
  hBase: number;
  hQuad: number;
  hOT: number;
  hBurn: number;
  /**
   * Flourishing band (v1.3.0 — the smoothed wellM step). The health dividend
   * scales LINEARLY from 0 at M ≤ wellLo to the full wellBonus at M ≥ wellHi.
   * The old binary M ≥ 70 flag was a +5.2%-of-estate cliff across a 0.02-point
   * post-ladder dial change — the same observable-threshold disease the
   * burnout band cured, one threshold up.
   */
  wellLo: number;
  wellHi: number;
  wellBonus: number;
  sabHealthBase: number;
  sabHealthSlope: number;
  frailH: number;
  frailMult: number;

  // micro cards (§23)
  microCardsPerYear: number;
  microCashCapFrac: number;
  microSpendPerPtLo: number;
  microSpendPerPtHi: number;
  microEarnPerPtLo: number;
  microEarnPerPtHi: number;
  microMoraleMin: number;
  microMoraleCap: number;

  // guards
  livingFracMax: number;

  // integration (§29–§30)
  substep: number;
}

/** The active plan (what income/morale/health integrate against each substep). */
export interface Policy {
  mode: Mode;
  livingFrac: number;
}

/**
 * The full mutable life state. Advanced by whole substeps; every field is a
 * number/boolean/array of numbers so `structuredClone`/JSON round-trips exactly.
 */
export interface State {
  /** completed substeps since startAge (integer) */
  n: number;
  S: number;
  M: number;
  H: number;
  /** highest completed skill */
  K: number;
  /** current job tier (promotion applied at the annual review) */
  J: number;
  /** partial study progress (study-years) */
  p: number;
  studying: boolean;
  /** skill whose tuition has been paid */
  enrolledSkill: number;
  /** cumulative sabbatical time since last skill completion (time-integrated gate) */
  sabYearsSinceSkill: number;
  /** Living locked to $0 (S fell below debtFloor); hysteresis release at debtRelease */
  brokeLock: boolean;
  alive: boolean;
  deathAge: number | null;
  policy: Policy;

  // accounting identity (§6) & profiling accumulators
  interestAccum: number;
  earnedAccum: number;
  colAccum: number;
  livingAccum: number;
  tuitionAccum: number;
  cardCashAccum: number;
  moraleInt: number;
  flourishYears: number;
  minM: number;
  minS: number;
  /** age at each skill completion */
  skillAges: number[];
}

/** What a strategy returns at the annual review (any field may be omitted). */
export interface Decision {
  mode?: Mode;
  livingFrac?: number;
  study?: boolean;
}

/**
 * A strategy bot: a pure function of (state, params, year) → decision. Returning
 * `void` is treated as an empty decision (std / 0 Living / no study).
 */
export type Strategy = (st: State, P: Params, year: number) => Decision | void;

/**
 * The normalized, fully-resolved plan the engine actually applies. This is the
 * unit recorded in the action log (persistence.ts) — the player's only input.
 */
export interface PlanAction {
  mode: Mode;
  livingFrac: number;
  study: boolean;
}

/** A generated micro-card (GDD §8E.1). Deck is a pure function of (seed, year). */
export interface CardEvent {
  /** within-year time in [0,1) */
  t: number;
  type: CardType;
  /** cash as a fraction of current income, clamped at accept time */
  cashFrac: number;
  morale: number;
}

/** One row in the per-year outcome history returned by a run. */
export interface YearRecord {
  age: number;
  S: number;
  M: number;
  H: number;
  K: number;
  mode: Mode;
  livingFrac: number;
}

/** Everything a single life produces — the harness/UI read model. */
export interface LifeResult {
  estate: number;
  deathAge: number;
  K: number;
  skillAges: number[];
  meanM: number;
  minM: number;
  flourishYears: number;
  minS: number;
  interestAccum: number;
  earnedAccum: number;
  colAccum: number;
  livingAccum: number;
  tuitionAccum: number;
  cardCashAccum: number;
  yearly: YearRecord[];
}

/**
 * One timestamped player input in the append-only action log.
 *
 * Two kinds (GDD §3.2 + §4.2):
 * - review (kind absent or 'review'): the annual plan filing at an integral
 *   age — full applyPlan semantics (promotion, enrolment, policy).
 * - adjust (kind 'adjust'): a mid-year change at any substep-aligned age —
 *   policy only (work mode + Living dial), no review side effects. "Yearly
 *   choice, changeable mid-year; all effects are rates."
 */
export interface LogEntry {
  /** in-game age at which this input applies (substep-aligned) */
  atAge: number;
  plan: PlanAction;
  kind?: 'review' | 'adjust';
}

/** Discriminates the two log-entry kinds (absent kind = review, back-compat). */
export const isReviewEntry = (e: LogEntry): boolean => e.kind !== 'adjust';

/** The card configuration for a run (deck seed + acceptance policy). */
export interface CardConfig {
  cards: boolean;
  cardSeed: number;
  cardPolicy: CardPolicy;
}

/** Options for a single simulated life. */
export interface SimOpts {
  cards?: boolean;
  cardSeed?: number;
  cardPolicy?: CardPolicy;
  /** if provided, each year's filed plan is appended here (the action log) */
  record?: LogEntry[];
}
